import type { TranslateService } from '@ngx-translate/core';
import type { GridColumn } from '../components/kl-grid/grid.models';
import { formatGridCellValue } from '../pipes/kl-grid-cell-value.pipe';

/** Ctrl/Cmd+C on a grid row, outside form fields and without a text selection (native copy wins then). */
export function isRowCopyShortcut(event: KeyboardEvent): boolean {
  if (!event.ctrlKey && !event.metaKey) return false;
  if (event.key.toLowerCase() !== 'c') return false;
  const target = event.target;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return false;
  if (target instanceof HTMLElement && target.isContentEditable) return false;
  return !window.getSelection()?.toString();
}

/** Tab-separated display values of the row's text columns, without a header line. */
export function gridRowCopyText(row: object, columns: GridColumn[], translate: TranslateService): string {
  return columns
    .filter((col) => !col.multi_select && !col.button && !col.icon && !col.icon_key && !col.icon_info)
    .map((col) => formatGridCellValue(row, col, translate))
    .join('\t');
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && globalThis.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through
  }
  return copyTextToClipboardFallback(text);
}

function copyTextToClipboardFallback(text: string): boolean {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
  return ok;
}
